import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useRowDensity, DENSITY_PRESETS } from "@/lib/useRowDensity";
import RowDensityToggle from "@/components/RowDensityToggle";
import { toast } from "sonner";
import {
  Inbox, FileWarning, BookOpenCheck, Sparkles, ArrowRight, AlertCircle, Building2, Flag,
} from "lucide-react";

export default function ReviewQueue() {
  const { cfg: densityCfg } = useRowDensity();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  const navigate = useNavigate();
  const { setSession } = useAuth();

  useEffect(() => {
    api.get("/memberships/queue")
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
  }, []);

  const jumpTo = async (tid, path) => {
    setSwitching(tid);
    try {
      const { data } = await api.post("/memberships/switch", { tenant_id: tid });
      setSession(data.token, data.user, data.tenant);
      toast.success(`Switched to ${data.tenant.name}`);
      navigate(path);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not switch");
    } finally {
      setSwitching(null);
    }
  };

  const s = data?.summary || {};
  const clients = data?.clients || [];
  const flaggedClients = clients.filter((c) => c.flagged);
  const otherClients = clients.filter((c) => !c.flagged);
  const totalWork = (s.statements_pending || 0) + (s.transactions_unreconciled || 0);

  return (
    <>
      <PageHeader
        title="Review queue"
        subtitle="Enterprise inbox"
        testid="queue-header"
        actions={<RowDensityToggle testid="queue-density" />}
      />
      <PageBody>
        <p className="text-sm text-[var(--ll-ink-soft)] max-w-prose mb-6">
          Cross-tenant inbox showing everything awaiting your attention across your client workspaces.
          Click any chip to jump straight into that workspace.
        </p>

        {loading && <div className="ll-progress mb-6" />}

        {!loading && data && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-px mb-8"
            style={{ background: "var(--ll-border)" }}
            data-testid="queue-summary"
          >
            <SummaryCell label="Clients with work" value={s.clients_with_work || 0} icon={Building2} tone="#EFF6FF" />
            <SummaryCell label="Statements pending" value={s.statements_pending || 0} icon={FileWarning} tone="#FEF2F2" />
            <SummaryCell label="Unreconciled txns" value={s.transactions_unreconciled || 0} icon={BookOpenCheck} tone="#FFF7ED" />
            <SummaryCell label="Flagged" value={s.flagged || 0} icon={Flag} tone="var(--ll-ochre-bg)" />
          </div>
        )}

        {!loading && totalWork === 0 && flaggedClients.length === 0 && (
          <div className="ll-card-flat p-12 text-center" data-testid="queue-empty">
            <Inbox size={32} className="mx-auto mb-3 opacity-40" />
            <div className="font-display font-bold text-xl mb-1">Inbox zero across every client</div>
            <div className="text-sm text-[var(--ll-ink-soft)]">
              Every statement is reconciled and every AI categorization is confirmed. Take the rest of the day off.
            </div>
          </div>
        )}

        {!loading && flaggedClients.length > 0 && (
          <div className="ll-card-flat mb-6 border-l-4" style={{ borderLeftColor: "var(--ll-ochre)" }} data-testid="queue-flagged-section">
            <div className="text-xs uppercase tracking-widest text-[var(--ll-ochre-ink)] bg-[var(--ll-ochre-bg)] px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--ll-border)" }}>
              <Flag size={12} fill="var(--ll-ochre)" strokeWidth={0} /> Flagged for review · {flaggedClients.length}
            </div>
            <ul>
              {flaggedClients.map((c) => (
                <QueueRow
                  key={c.tenant.id}
                  c={c}
                  switching={switching}
                  jumpTo={jumpTo}
                  densityCfg={densityCfg}
                  highlight
                />
              ))}
            </ul>
          </div>
        )}

        {!loading && otherClients.length > 0 && (
          <div className="ll-card-flat" data-testid="queue-list">
            <div className="text-xs uppercase tracking-widest text-[var(--ll-ink-muted)] px-5 py-3 border-b" style={{ borderColor: "var(--ll-border)" }}>
              By client · sorted by workload
            </div>
            <ul>
              {otherClients.map((c) => (
                <QueueRow
                  key={c.tenant.id}
                  c={c}
                  switching={switching}
                  jumpTo={jumpTo}
                  densityCfg={densityCfg}
                />
              ))}
            </ul>
          </div>
        )}
      </PageBody>
    </>
  );
}

function QueueRow({ c, switching, jumpTo, densityCfg = DENSITY_PRESETS.cozy, highlight = false }) {
  // Map the shared row-density tdY (which targets table cells) to a slightly
  // more generous card padding since each row here holds multiple chips.
  const rowPad = densityCfg.tdY === "py-1" ? "py-2" : densityCfg.tdY === "py-4" ? "py-5" : "py-4";
  return (
    <li
      className={`px-5 ${rowPad} border-b grid grid-cols-1 md:grid-cols-[1.5fr,3fr,auto] gap-3 items-center`}
      style={{
        borderColor: "var(--ll-border)",
        background: highlight ? "rgba(254,243,199,0.25)" : undefined,
      }}
      data-testid={`queue-row-${c.tenant.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className="w-9 h-9 grid place-items-center text-white shrink-0"
          style={{ background: c.tenant.brand_color || "var(--ll-ink)" }}
        >
          <Building2 size={15} />
        </div>
        <div className="min-w-0">
          <div className="font-display font-bold leading-tight truncate flex items-center gap-1.5">
            {c.tenant.name}
            {c.flagged && <Flag size={11} fill="var(--ll-ochre)" strokeWidth={0} className="shrink-0" />}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)]">
            {c.flagged && c.flag_reason ? `Note · ${c.flag_reason}` : `Role · ${c.role}`}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {c.statements_pending > 0 && (
          <Chip
            icon={FileWarning}
            tone="#FEF2F2"
            text={`${c.statements_pending} statement${c.statements_pending === 1 ? "" : "s"} need review`}
            onClick={() => jumpTo(c.tenant.id, "/app/statements")}
            testid={`chip-stmts-${c.tenant.id}`}
          />
        )}
        {c.transactions_unreconciled > 0 && (
          <Chip
            icon={BookOpenCheck}
            tone="#FFF7ED"
            text={`${c.transactions_unreconciled} unreconciled`}
            onClick={() => jumpTo(c.tenant.id, "/app/reconciliation")}
            testid={`chip-recon-${c.tenant.id}`}
          />
        )}
        {c.ai_categorized > 0 && (
          <Chip
            icon={Sparkles}
            tone="#FEFCE8"
            text={`${c.ai_categorized} AI categorized`}
            onClick={() => jumpTo(c.tenant.id, "/app/transactions")}
            testid={`chip-ai-${c.tenant.id}`}
          />
        )}
        {c.failed_statements > 0 && (
          <Chip
            icon={AlertCircle}
            tone="#FECACA"
            text={`${c.failed_statements} failed parse`}
            onClick={() => jumpTo(c.tenant.id, "/app/statements")}
            testid={`chip-failed-${c.tenant.id}`}
          />
        )}
        {c.flagged && c.statements_pending === 0 && c.transactions_unreconciled === 0 && c.failed_statements === 0 && (
          <span className="text-xs px-3 py-1.5 border flex items-center gap-1.5 text-[var(--ll-ink-soft)]" style={{ background: "#FEFCE8", borderColor: "var(--ll-border)" }}>
            <Flag size={12} /> Pinned · no outstanding work
          </span>
        )}
      </div>

      <button
        onClick={() => jumpTo(c.tenant.id, "/app/dashboard")}
        disabled={switching === c.tenant.id}
        data-testid={`open-workspace-${c.tenant.id}`}
        className="text-sm px-3 py-2 bg-[var(--ll-ink)] text-white hover:bg-[#2d6a4f] transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60 w-full md:w-auto"
      >
        {switching === c.tenant.id ? "Switching…" : "Open"} <ArrowRight size={13} />
      </button>
    </li>
  );
}

function SummaryCell({ label, value, icon: Icon, tone }) {
  return (
    <div
      className="p-5"
      style={{ background: tone }}
      data-testid={`queue-summary-${String(label).toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">{label}</div>
        <Icon size={14} className="opacity-70" />
      </div>
      <div className="font-mono-fin text-3xl leading-none">{value.toLocaleString()}</div>
    </div>
  );
}

function Chip({ icon: Icon, text, tone, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="text-xs px-3 py-1.5 border flex items-center gap-1.5 transition-colors hover:bg-white"
      style={{ background: tone, borderColor: "var(--ll-border)" }}
    >
      <Icon size={12} /> {text}
    </button>
  );
}
